import { useEffect, useState } from 'preact/hooks';
import { ApiError, api } from '../../lib/api.js';
import { Button } from '../../components/Button.jsx';
import { MachinePicker } from './MachinePicker.jsx';
import { DAYS_OF_WEEK } from './maintenance-shared.js';

const todayISO = () => new Date().toISOString().slice(0, 10);
const trimOrNull = (v) => { const s = (v ?? '').trim(); return s === '' ? null : s; };
const toDateInput = (iso) => (iso ? new Date(iso).toISOString().slice(0, 10) : '');

/** Create or edit a recurring schedule. Pass `schedule` to switch to edit mode. */
export const NewScheduleModal = ({ open, onClose, onCreated, onSaved, defaultMachine, schedule }) => {
  const editing = !!schedule;

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [kind, setKind] = useState('routine');
  const [machine, setMachine] = useState({ printerId: null, equipmentId: null });
  const [frequency, setFrequency] = useState('weekly');
  const [dayOfWeek, setDayOfWeek] = useState(5);
  const [dayOfMonth, setDayOfMonth] = useState(1);
  const [intervalDays, setIntervalDays] = useState(7);
  const [nextDueAt, setNextDueAt] = useState(todayISO());
  const [active, setActive] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    if (editing) {
      setTitle(schedule.title ?? '');
      setDescription(schedule.description ?? '');
      setKind(schedule.kind ?? 'routine');
      setFrequency(schedule.frequency ?? 'weekly');
      setDayOfWeek(schedule.dayOfWeek ?? 5);
      setDayOfMonth(schedule.dayOfMonth ?? 1);
      setIntervalDays(schedule.intervalDays ?? 7);
      setNextDueAt(toDateInput(schedule.nextDueAt) || todayISO());
      setActive(schedule.active ?? true);
      setMachine({ printerId: schedule.printerId, equipmentId: schedule.equipmentId });
    } else {
      setTitle(''); setDescription(''); setKind('routine');
      setFrequency('weekly'); setDayOfWeek(5); setDayOfMonth(1); setIntervalDays(7);
      setNextDueAt(todayISO()); setActive(true);
      setMachine(defaultMachine ?? { printerId: null, equipmentId: null });
    }
  }, [open, editing, schedule, defaultMachine]);

  if (!open) return null;

  const save = async () => {
    if (!title.trim()) return;
    if (!editing && !machine.printerId && !machine.equipmentId) {
      setError({ message: 'Pick a machine' }); return;
    }
    setBusy(true);
    setError(null);
    try {
      const body = {
        title: title.trim(),
        description: trimOrNull(description),
        kind,
        frequency,
        dayOfWeek: frequency === 'weekly' ? dayOfWeek : null,
        dayOfMonth: frequency === 'monthly' ? dayOfMonth : null,
        intervalDays: frequency === 'every_n_days' ? intervalDays : null,
        nextDueAt: nextDueAt || null,
        active,
      };
      if (editing) {
        const updated = await api.patch(`/maintenance/schedules/${schedule.id}`, body);
        onSaved?.(updated);
      } else {
        const created = await api.post('/maintenance/schedules', {
          ...body,
          printerId: machine.printerId,
          equipmentId: machine.equipmentId,
        });
        onCreated?.(created);
      }
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!editing) return;
    if (!window.confirm(`Delete recurring schedule "${schedule.title}"?`)) return;
    try {
      await api.delete(`/maintenance/schedules/${schedule.id}`);
      onSaved?.();
    } catch (err) {
      alert(err.message ?? 'Failed to delete');
    }
  };

  return (
    <div
      class="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/55 px-4 py-10"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div class="w-full max-w-md overflow-hidden rounded-xl border border-border-subtle bg-bg-base">
        <div class="border-b border-border-subtle bg-bg-header px-5 py-4">
          <p class="eyebrow mb-1" style={{ color: '#E63B7A' }}>
            {editing ? 'Edit recurring schedule' : 'New recurring schedule'}
          </p>
        </div>

        <div class="space-y-4 p-5">
          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Title</p>
            <input
              autoFocus type="text" value={title}
              onInput={(e) => setTitle(e.currentTarget.value)}
              placeholder="e.g. Roland weekly clean"
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary placeholder:text-text-muted focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">
              Machine
              {editing && <span class="ml-2 font-medium text-text-muted">· locked — recreate to change</span>}
            </p>
            <MachinePicker value={machine} onChange={setMachine} disabled={editing} />
          </div>

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Kind</p>
            <div class="flex overflow-hidden rounded-md border border-border-strong">
              <button type="button" onClick={() => setKind('routine')}
                class={`flex-1 px-3 py-1.5 text-[12px] font-semibold ${kind === 'routine' ? 'bg-status-ok text-[#0D2E1E]' : 'text-text-tertiary'}`}>
                Routine (in-house)
              </button>
              <button type="button" onClick={() => setKind('visit')}
                class={`flex-1 px-3 py-1.5 text-[12px] font-semibold ${kind === 'visit' ? 'bg-status-purple text-[#1A0F2E]' : 'text-text-tertiary'}`}>
                Visit (external tech)
              </button>
            </div>
          </div>

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Frequency</p>
            <select value={frequency} onChange={(e) => setFrequency(e.currentTarget.value)}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand">
              <option value="daily">Daily</option>
              <option value="weekly">Weekly on…</option>
              <option value="monthly">Monthly on day…</option>
              <option value="every_n_days">Every N days</option>
            </select>
          </div>

          {frequency === 'weekly' && (
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Day of week</p>
              <div class="flex overflow-hidden rounded-md border border-border-strong">
                {DAYS_OF_WEEK.map((d, i) => (
                  <button key={d} type="button" onClick={() => setDayOfWeek(i)}
                    class={`flex-1 px-2 py-1.5 text-[11px] font-semibold ${dayOfWeek === i ? 'bg-status-ok text-[#0D2E1E]' : 'text-text-tertiary hover:bg-bg-card'}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
          )}

          {frequency === 'monthly' && (
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Day of month (1–31)</p>
              <input type="number" min={1} max={31} value={dayOfMonth} onInput={(e) => setDayOfMonth(Number(e.currentTarget.value))}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
          )}

          {frequency === 'every_n_days' && (
            <div>
              <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Interval (days)</p>
              <input type="number" min={1} value={intervalDays} onInput={(e) => setIntervalDays(Number(e.currentTarget.value))}
                class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
              />
            </div>
          )}

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Next due date</p>
            <input type="date" value={nextDueAt} onInput={(e) => setNextDueAt(e.currentTarget.value)}
              class="block w-full rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          <label class="flex cursor-pointer items-center gap-2">
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.currentTarget.checked)}
              class="h-4 w-4 rounded border-border-strong bg-bg-card" />
            <span class="text-[13px] font-medium text-text-body">Active</span>
          </label>

          <div>
            <p class="mb-1 text-[12px] font-semibold text-text-tertiary">Description <span class="font-medium text-text-muted">· optional</span></p>
            <textarea rows={2} value={description} onInput={(e) => setDescription(e.currentTarget.value)}
              class="block w-full resize-none rounded-md border border-border-strong bg-bg-card px-3 py-2 text-sm font-medium text-text-primary focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand"
            />
          </div>

          {error && (
            <p class="text-[13px] font-semibold text-ink-magenta">
              {error instanceof ApiError ? error.message : (error.message ?? 'Failed to save')}
            </p>
          )}
        </div>

        <div class="flex items-center justify-between gap-2 border-t border-border-subtle bg-bg-header px-5 py-3">
          {editing && (
            <Button variant="ghost" onClick={remove} class="text-ink-magenta hover:text-ink-magenta">Delete</Button>
          )}
          <div class="ml-auto flex gap-2">
            <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
            <Button variant="primary" onClick={save} disabled={busy || !title.trim()}>
              {busy ? 'Saving…' : editing ? 'Save changes' : 'Create schedule'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
